import type { AuthoredCard, CardColor } from "../schemas"

// Mirrors the deck validation constants
const REQUIRED_MERC_COUNT = 24
const MAX_COPIES_PER_CARD = 3
const MIN_LOW_COST_MERCS = 6
const MAX_HIGH_COST_MERCS = 8
const LOW_COST_THRESHOLD = 3
const HIGH_COST_THRESHOLD = 7

type EntryMap = Map<string, number>

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5)
}

function pickAvailable(
  pool: AuthoredCard[],
  copies: EntryMap,
  highCount: number,
): AuthoredCard | null {
  for (const card of shuffle(pool)) {
    if ((copies.get(card.id) ?? 0) >= MAX_COPIES_PER_CARD) continue
    const cost = card.derivedCost ?? 0
    if (cost >= HIGH_COST_THRESHOLD && highCount >= MAX_HIGH_COST_MERCS)
      continue
    return card
  }
  return null
}

/**
 * Build a random EntryMap from authored cards, respecting deck composition rules
 * as closely as possible given the available card pool.
 *
 * @param authoredCards - Pool of authored_unit cards to draw from
 * @param colorPreference - Optional: restrict pool to cards matching this color
 */
export function buildRandomDeck(
  authoredCards: AuthoredCard[],
  colorPreference?: CardColor,
): EntryMap {
  let pool = authoredCards.filter((c) => c.kind === "authored_unit")
  if (colorPreference) {
    const filtered = pool.filter(
      (c) =>
        c.primaryColor === colorPreference ||
        c.secondaryColor === colorPreference,
    )
    // Fall back to the full pool if the color filter leaves too few cards
    if (filtered.length >= 4) pool = filtered
  }

  if (pool.length === 0) return new Map()

  const copies: EntryMap = new Map()
  let highCount = 0

  const total = () => [...copies.values()].reduce((s, n) => s + n, 0)

  const add = (card: AuthoredCard): boolean => {
    const current = copies.get(card.id) ?? 0
    if (current >= MAX_COPIES_PER_CARD) return false
    const cost = card.derivedCost ?? 0
    if (cost >= HIGH_COST_THRESHOLD && highCount >= MAX_HIGH_COST_MERCS)
      return false
    copies.set(card.id, current + 1)
    if (cost >= HIGH_COST_THRESHOLD) highCount++
    return true
  }

  // Phase 1: guarantee ≥ MIN_LOW_COST_MERCS low-cost slots
  const lowPool = pool.filter((c) => (c.derivedCost ?? 0) <= LOW_COST_THRESHOLD)
  const lowSource = lowPool.length > 0 ? lowPool : pool

  for (
    let i = 0;
    i < MIN_LOW_COST_MERCS && total() < REQUIRED_MERC_COUNT;
    i++
  ) {
    for (let attempt = 0; attempt < pool.length * 2; attempt++) {
      const card = shuffle(lowSource)[0]
      if (card && add(card)) break
    }
  }

  // Phase 2: fill remaining slots randomly from the full pool (respecting high cap)
  let stalls = 0
  while (total() < REQUIRED_MERC_COUNT) {
    const card = pickAvailable(pool, copies, highCount)
    if (!card) {
      stalls++
      if (stalls > 10) break // pool exhausted
      continue
    }
    stalls = 0
    add(card)
  }

  return copies
}
